import { apiClient } from "./core/client";

// ─── Types ─────────────────────────────────────────────────────

export interface DailyClosingInvoice {
  id: string;
  invoiceNumber: number;
  customerName: string;
  total: number;
  subtotal: number;
  taxAmount: number;
  status: string;
  paymentMethod: string | null;
  createdAt: string;
}

export interface DailyClosingData {
  date: string;
  invoices: DailyClosingInvoice[];
  summary: {
    totalInvoiced: number;
    totalPaid: number;
    totalPending: number;
    countPaid: number;
    countPending: number;
    countTotal: number;
    byPaymentMethod: { cash: number; card: number; bank_transfer: number };
  };
}

export interface RevenuePeriod {
  period: string;
  periodLabel: string;
  revenue: number;
  invoiceCount: number;
  byPaymentMethod: { cash: number; card: number; bank_transfer: number };
}

export interface RevenueSummaryData {
  from: string;
  to: string;
  groupBy: string;
  periods: RevenuePeriod[];
  totals: { revenue: number; invoiceCount: number };
  comparison: { previousPeriodRevenue: number; changePercent: number };
}

export interface ReceivableInvoice {
  id: string;
  invoiceNumber: number;
  total: number;
  dueDate: string;
  daysOverdue: number;
  agingBucket: string;
}

export interface ReceivableCustomer {
  customerId: string;
  customerName: string;
  totalOutstanding: number;
  invoices: ReceivableInvoice[];
}

export interface AccountsReceivableData {
  asOfDate: string;
  customers: ReceivableCustomer[];
  agingSummary: {
    current: number;
    days30_60: number;
    days60_90: number;
    days90Plus: number;
    totalOutstanding: number;
  };
}

export interface VatMonth {
  month: string;
  monthLabel: string;
  taxableAmount: number;
  vatCollected: number;
  totalWithVat: number;
  invoiceCount: number;
}

export interface VatReportData {
  quarter: number;
  year: number;
  quarterLabel: string;
  from: string;
  to: string;
  months: VatMonth[];
  totals: {
    taxableAmount: number;
    vatCollected: number;
    totalWithVat: number;
    invoiceCount: number;
    effectiveVatRate: number;
  };
  comparison: {
    previousQuarter: string;
    previousVatCollected: number;
    changePercent: number;
  };
}

// ─── API Calls ─────────────────────────────────────────────────

export async function getDailyClosing(date?: string) {
  const params: Record<string, string> = {};
  if (date) params.date = date;
  const { data } = await apiClient.get<DailyClosingData>("/api/reports/daily-closing", { params });
  return data;
}

export async function getRevenueSummary(from: string, to: string, groupBy: string = "day") {
  const { data } = await apiClient.get<RevenueSummaryData>("/api/reports/revenue-summary", {
    params: { from, to, groupBy },
  });
  return data;
}

export async function getAccountsReceivable(asOfDate?: string) {
  const params: Record<string, string> = {};
  if (asOfDate) params.asOfDate = asOfDate;
  const { data } = await apiClient.get<AccountsReceivableData>("/api/reports/accounts-receivable", { params });
  return data;
}

export async function getVatReport(quarter: number, year: number) {
  const { data } = await apiClient.get<VatReportData>("/api/reports/vat", {
    params: { quarter, year },
  });
  return data;
}

export function downloadReportCsv(endpoint: string, params: Record<string, string>, filename: string) {
  const searchParams = new URLSearchParams({ ...params, format: "csv" });
  apiClient
    .get(`/api/reports/${endpoint}?${searchParams.toString()}`, { responseType: "blob" })
    .then((res) => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    });
}
